import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { BrowserMultiFormatReader } from '@zxing/browser';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScannerOutlined';

// Dialogo de escaneo de codigo de barras: acepta tanto un lector fisico USB
// (que funciona como teclado y "escribe" el codigo seguido de Enter) como la
// camara del dispositivo (laptop o celular) usando ZXing para decodificar el video.
export default function BarcodeScannerDialog({ open, onClose, onDetected }) {
  const [manualValue, setManualValue] = useState('');
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState('');

  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const inputRef = useRef(null);

  const stopCamera = () => {
    controlsRef.current?.stop();
    controlsRef.current = null;
    setCameraActive(false);
  };

  useEffect(() => {
    if (!open) {
      setManualValue('');
      setCameraError('');
      stopCamera();
      return;
    }

    // Enfoca el input apenas se abre, para que un lector USB pueda escribir de inmediato.
    const focusTimeout = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(focusTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => stopCamera, []);

  const handleManualSubmit = (event) => {
    event.preventDefault();
    const code = manualValue.trim();
    if (!code) {
      return;
    }
    stopCamera();
    onDetected(code);
  };

  const startCamera = async () => {
    setCameraError('');
    try {
      const codeReader = new BrowserMultiFormatReader();

      // Preferir la camara trasera en celulares, si el navegador expone varios dispositivos.
      let deviceId;
      try {
        const devices = await BrowserMultiFormatReader.listVideoInputDevices();
        const rearCamera = devices.find((device) => /back|tras|rear|environment/i.test(device.label));
        deviceId = (rearCamera || devices[devices.length - 1])?.deviceId;
      } catch (enumerationError) {
        deviceId = undefined;
      }

      setCameraActive(true);

      const controls = await codeReader.decodeFromVideoDevice(deviceId, videoRef.current, (result, error) => {
        if (result) {
          controls.stop();
          controlsRef.current = null;
          setCameraActive(false);
          onDetected(result.getText());
        }
        // Los errores "NotFoundException" son normales: significan que ese frame no tenia un codigo legible.
      });

      controlsRef.current = controls;
    } catch (error) {
      setCameraActive(false);
      setCameraError(
        'No se pudo acceder a la cámara. Verifica los permisos del navegador o usa un lector físico.'
      );
    }
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Escanear código de barras</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Stack component="form" spacing={1} onSubmit={handleManualSubmit}>
            <Typography variant="body2" color="text.secondary">
              Usa un lector físico: apunta y dispara sobre este campo, se completará solo.
            </Typography>
            <TextField
              inputRef={inputRef}
              autoFocus
              fullWidth
              label="Código escaneado"
              value={manualValue}
              onChange={(e) => setManualValue(e.target.value)}
            />
          </Stack>

          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              O usa la cámara de esta laptop o de tu celular:
            </Typography>

            {!cameraActive && (
              <Button variant="outlined" startIcon={<QrCodeScannerIcon />} onClick={startCamera}>
                Activar cámara
              </Button>
            )}

            {cameraError && <Alert severity="error">{cameraError}</Alert>}

            <Box
              sx={{
                display: cameraActive ? 'block' : 'none',
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: 'common.black',
              }}
            >
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video ref={videoRef} style={{ width: '100%', display: 'block' }} muted />
            </Box>

            {cameraActive && (
              <Button onClick={stopCamera} color="inherit">
                Detener cámara
              </Button>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleManualSubmit} disabled={!manualValue.trim()}>
          Usar código
        </Button>
      </DialogActions>
    </Dialog>
  );
}

BarcodeScannerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDetected: PropTypes.func.isRequired,
};
